
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Mail, Phone, MapPin, Send, MessageSquare as MessageSquareIcon, MessageCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { validateEmail, validateName, validateMessage } from '@/utils/security';
import { sendEmail } from '@/utils/emailService';
import { useForm } from '@/hooks/useForm';
import { fadeIn } from '@/utils/animations';

const ContactPage = () => {
  const { toast } = useToast();
  const location = useLocation();

  // Form validation rules
  const validationRules = {
    name: {
      required: true,
      validator: validateName,
      requiredMessage: "Voer een geldige naam in",
      message: "Naam moet tussen 2-100 karakters bevatten, alleen letters"
    },
    email: {
      required: true,
      validator: validateEmail,
      requiredMessage: "Voer een geldig e-mailadres in",
      message: "Voer een geldig e-mailadres in"
    },
    message: {
      required: true,
      validator: validateMessage,
      requiredMessage: "Voer een bericht in",
      message: "Uw bericht moet tussen 10 en 2000 karakters bevatten"
    }
  };

  const {
    formData,
    isSubmitting,
    errors,
    handleChange,
    handleSubmit,
    setFormData
  } = useForm({
    name: '',
    email: '',
    message: '',
    subject: ''
  }, validationRules);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subjectFromQuery = params.get('subject');
    if (subjectFromQuery === 'DemoAanvraag') {
      setFormData(prev => ({ 
        ...prev, 
        subject: 'Demo Aanvraag voor Classbuddy', 
        message: 'Graag ontvang ik meer informatie en/of een demo van Classbuddy voor onze school.' 
      }));
    }
  }, [location.search, setFormData]);

  const contactInfo = [
    { 
      icon: <Mail size={28} className="text-cm-pink" />, 
      text: "info@classbuddy.nl", 
      href: "mailto:info@classbuddy.nl", 
      label: "E-mail ons" 
    },
    { 
      icon: <Phone size={28} className="text-cm-yellow" />, 
      text: "06-27165899", 
      href: "tel:0627165899", 
      label: "Bel ons direct" 
    },
    { 
      icon: <MessageCircle size={28} className="text-green-500" />, 
      text: "06-27165899", 
      href: "https://wa.me/31627165899", 
      label: "Stuur ons een WhatsApp",
      subtext: "Bereikbaar op schooldagen tussen 08:00 – 17:00"
    },
    { 
      icon: <MapPin size={28} className="text-cm-green" />, 
      text: "Keurenplein 41, 1069CD Amsterdam", 
      label: "Bezoekadres (op afspraak)" 
    },
  ];

  const onSubmit = async (sanitizedData) => {
    try {
      // EmailJS template parameters
      const templateParams = {
        name: sanitizedData.name,
        email: sanitizedData.email,
        message: sanitizedData.message,
        title: sanitizedData.subject || "Contactformulier",
        time: new Date().toLocaleString('nl-NL')
      };
      
      const emailResult = await sendEmail(templateParams);
      
      if (emailResult.success) {
        toast({
          title: "Bedankt voor je bericht! ✅",
          description: "Je bericht is automatisch verzonden naar info@classbuddy.nl. We nemen snel contact op!",
          duration: 6000,
          className: "bg-green-50 text-green-800 border-green-200",
        });
        return { success: true };
      } else {
        throw new Error(emailResult.error || 'Email kon niet verzonden worden');
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "EmailJS configuratie vereist ⚙️",
        description: "De email service is nog niet volledig geconfigureerd. Controleer de console voor setup instructies.",
        variant: "destructive",
        duration: 10000,
      });
      return { success: false };
    }
  };

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-8">
          <MessageSquareIcon size={72} className="text-cm-pink" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title"
        >
          Vragen? Ideeën? <span className="text-cm-blue-dark font-medium">Samenwerken?</span>
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle"
        >
          Wij staan altijd open voor een goed gesprek! Of u nu een ouder, leerkracht, schoolbestuurder of potentiële partner bent, uw input is waardevol. Neem gerust contact op.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start">
        <motion.div variants={fadeIn(0.3, 20)} initial="hidden" animate="visible" className="space-y-8 lg:col-span-2">
          <h2 className="text-4xl font-heading font-medium text-cm-blue-dark mb-8">Direct Contact</h2>
          {contactInfo.map((item, index) => (
            <motion.div 
              key={index} 
              variants={fadeIn(0.35 + index * 0.1, 15)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 flex items-start space-x-5"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cm-blue-light to-white rounded-full flex items-center justify-center shadow-md">
                {item.icon}
              </div>
              <div>
                <p className="text-sm text-cm-gray font-semibold">{item.label}</p>
                {item.href ? (
                  <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-lg text-cm-blue hover:text-cm-pink transition-colors font-medium block break-words">
                    {item.text}
                  </a>
                ) : (
                  <p className="text-lg text-cm-gray-dark font-medium block break-words">{item.text}</p>
                )}
                {item.subtext && (
                  <p className="text-xs text-cm-gray mt-1">{item.subtext}</p>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div variants={fadeIn(0.4, 20)} initial="hidden" animate="visible" className="glassmorphism-card p-8 sm:p-10 rounded-2xl shadow-2xl lg:col-span-3">
          <h2 className="text-4xl font-heading font-medium text-cm-blue-dark mb-8">Stuur ons een bericht</h2>
          <form onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(onSubmit);
          }} className="space-y-6">
            <div>
              <Label htmlFor="name" className="text-cm-gray-dark font-medium text-lg">Naam</Label>
              <Input 
                type="text" 
                id="name" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                required 
                maxLength={100}
                className="mt-2 p-3 text-md rounded-lg bg-white/70 border-cm-blue-light focus:ring-cm-pink" 
                placeholder="Uw volledige naam" 
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label htmlFor="email" className="text-cm-gray-dark font-medium text-lg">E-mailadres</Label>
              <Input 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required 
                maxLength={254}
                className="mt-2 p-3 text-md rounded-lg bg-white/70 border-cm-blue-light focus:ring-cm-pink" 
                placeholder="voorbeeld@email.com" 
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label htmlFor="subject" className="text-cm-gray-dark font-medium text-lg">Onderwerp</Label>
              <Input 
                type="text" 
                id="subject" 
                name="subject" 
                value={formData.subject} 
                onChange={handleChange} 
                maxLength={200}
                className="mt-2 p-3 text-md rounded-lg bg-white/70 border-cm-blue-light focus:ring-cm-pink" 
                placeholder="Bijv. Vraag over Classbuddy, Demo Aanvraag" 
              />
            </div>
            <div>
              <Label htmlFor="message" className="text-cm-gray-dark font-medium text-lg">Uw Bericht</Label>
              <Textarea 
                id="message" 
                name="message" 
                value={formData.message} 
                onChange={handleChange} 
                rows={6} 
                required 
                maxLength={2000}
                className="mt-2 p-3 text-md rounded-lg bg-white/70 border-cm-blue-light focus:ring-cm-pink" 
                placeholder="Typ hier uw vraag of opmerking..." 
              />
              {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
            </div>
            <Button 
              type="submit" 
              size="lg" 
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-cm-pink to-cm-blue hover:from-cm-pink-dark hover:to-cm-blue-dark text-white font-medium text-xl py-4 rounded-xl shadow-xl transform hover:scale-102 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? 'Verzenden...' : 'Bericht Versturen'} 
              <Send size={22} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;
